import { supabase } from '@/lib/supabase'
import type { PostAttachment } from '@/modules/post-shared'

export type VisionPostAttachment = PostAttachment

function mapRow(row: any): VisionPostAttachment | null {
  const id = Number(row?.id)
  const visionId = Number(row?.vision_id)
  const url = String(row?.url || '').trim()
  if (!Number.isFinite(id) || id <= 0) return null
  if (!Number.isFinite(visionId) || visionId <= 0) return null
  if (!url) return null

  return {
    id,
    postId: visionId,
    url,
    storageBucket:
      typeof row?.storage_bucket === 'string' || row?.storage_bucket === null
        ? (row.storage_bucket as string | null)
        : null,
    storagePath:
      typeof row?.storage_path === 'string' || row?.storage_path === null
        ? (row.storage_path as string | null)
        : null,
    contentType:
      typeof row?.content_type === 'string' || row?.content_type === null
        ? (row.content_type as string | null)
        : null,
    sizeBytes:
      row?.size_bytes === null || row?.size_bytes === undefined
        ? null
        : Number.isFinite(Number(row.size_bytes))
          ? Number(row.size_bytes)
          : null,
    createdAt: row?.created_at ? String(row.created_at) : '',
  }
}

function normalizeIds(idsRaw: number[]): number[] {
  const out: number[] = []
  const seen = new Set<number>()
  for (const raw of idsRaw || []) {
    const id = Number(raw)
    if (!Number.isFinite(id) || id <= 0) continue
    const normalized = Math.floor(id)
    if (seen.has(normalized)) continue
    seen.add(normalized)
    out.push(normalized)
  }
  return out
}

export async function fetchVisionPostAttachmentsByVisionIds(
  visionIds: number[],
): Promise<Record<string, VisionPostAttachment[]>> {
  const ids = normalizeIds(visionIds)
  if (!ids.length) return {}

  try {
    const { data, error } = await supabase
      .from('market_vision_attachments' as any)
      .select('id,vision_id,url,storage_bucket,storage_path,content_type,size_bytes,created_at')
      .in('vision_id', ids as any)
      .order('created_at', { ascending: true })

    if (error) {
      // eslint-disable-next-line no-console
      console.error('fetchVisionPostAttachmentsByVisionIds error', error)
      return {}
    }

    const rows = Array.isArray(data) ? data : []
    const map: Record<string, VisionPostAttachment[]> = {}

    rows.forEach((row: any) => {
      const mapped = mapRow(row)
      if (!mapped) return
      const key = String(mapped.postId)
      if (!map[key]) map[key] = []
      map[key].push(mapped)
    })

    Object.keys(map).forEach((key) => {
      map[key].sort((a, b) => a.id - b.id)
    })

    return map
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('fetchVisionPostAttachmentsByVisionIds unexpected error', error)
    return {}
  }
}

export async function addVisionPostAttachments(
  visionId: number,
  items: {
    url: string
    storageBucket?: string | null
    storagePath?: string | null
    contentType?: string | null
    sizeBytes?: number | null
  }[],
): Promise<VisionPostAttachment[]> {
  const id = Number(visionId)
  if (!Number.isFinite(id) || id <= 0) return []

  const rows = (items || [])
    .map((item) => {
      const url = String(item.url || '').trim()
      if (!url) return null
      return {
        vision_id: id,
        url,
        storage_bucket: item.storageBucket ?? null,
        storage_path: item.storagePath ?? null,
        content_type: item.contentType ?? null,
        size_bytes:
          typeof item.sizeBytes === 'number' && Number.isFinite(item.sizeBytes)
            ? Math.max(0, Math.floor(item.sizeBytes))
            : item.sizeBytes == null
              ? null
              : Number(item.sizeBytes) || null,
      }
    })
    .filter(Boolean) as any[]

  if (!rows.length) return []

  const { data, error } = await supabase
    .from('market_vision_attachments' as any)
    .insert(rows as any)
    .select('id,vision_id,url,storage_bucket,storage_path,content_type,size_bytes,created_at')

  if (error) {
    // eslint-disable-next-line no-console
    console.error('addVisionPostAttachments error', error)
    throw error
  }

  return (Array.isArray(data) ? data : []).map(mapRow).filter(Boolean) as VisionPostAttachment[]
}

export async function deleteVisionPostAttachmentsByIds(idsRaw: number[]): Promise<void> {
  const ids = normalizeIds(idsRaw)
  if (!ids.length) return

  const { error } = await supabase
    .from('market_vision_attachments' as any)
    .delete()
    .in('id', ids as any)

  if (error) {
    // eslint-disable-next-line no-console
    console.error('deleteVisionPostAttachmentsByIds error', error)
    throw error
  }
}
